import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Separator } from "@/components/ui/separator";

const COMPARISON_URL = "/images/shift-comparison.jpg";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.7 },
};

export default function TheShift() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["5%", "-5%"]);

  return (
    <section
      id="shift"
      ref={ref}
      data-testid="shift-section"
      className="relative py-24 md:py-36 bg-[#0a192f]"
    >
      <div className="blueprint-grid absolute inset-0 opacity-10" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Label */}
        <motion.p
          {...fadeInUp}
          className="font-body text-fluid-caption tracking-[0.3em] uppercase text-gold mb-4"
        >
          The Shift
        </motion.p>

        <motion.h2
          {...fadeInUp}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-heading font-bold text-fluid-h2 uppercase tracking-tight text-slate-50 mb-6"
        >
          From Consultant
          <br />
          <span className="text-gold-gradient">To Master Business Craftsman</span>
        </motion.h2>

        <Separator className="bg-gold/20 mb-12 max-w-xs" />

        {/* Text */}
        <div className="space-y-6 max-w-3xl">
          <motion.p {...fadeInUp} transition={{ duration: 0.7, delay: 0.2 }} className="font-body text-fluid-body-lg text-slate-300">
            You do good work. You show up. You take care of your customers.
            Your systems should do the same.
          </motion.p>
          <motion.p {...fadeInUp} transition={{ duration: 0.7, delay: 0.3 }} className="font-body text-fluid-body-lg text-slate-300">
            White Beard Solutions is my personal brand. My name is{" "}
            <a
              href="https://ph.mba"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-50 font-semibold underline underline-offset-2 hover:text-gold transition-colors duration-300"
            >
              Paul Hughes
            </a>. I build and install Smart Digital Doorways through{" "}
            <a
              href="https://diyb.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold font-semibold underline underline-offset-2 hover:text-gold-300 transition-colors duration-300"
            >
              Doorways Into Your Business (DIYB)
            </a>.
          </motion.p>
          <motion.p {...fadeInUp} transition={{ duration: 0.7, delay: 0.4 }} className="font-body text-fluid-body-lg text-slate-300">
            A Smart Digital Doorway acts like a quiet extra set of hands.
            It works in the background. It helps the right customer take the next step.
          </motion.p>
          <motion.p {...fadeInUp} transition={{ duration: 0.7, delay: 0.5 }} className="font-body text-fluid-body-lg text-slate-300">
            I custom-fit each build to the business in front of me.
            I install it so it works every day. If you run a local business in
            Kingsville, Ontario or the Windsor-Essex Region{" "}
            <a
              href="https://diyb.ca/contact-diyb/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold hover:text-gold-300 underline underline-offset-2 transition-colors duration-300"
            >
              contact me
            </a>{" "}
            and let's talk about smart digital solutions for your business.
          </motion.p>
        </div>

        {/* Image - centered below text */}
        <motion.div
          className="relative mt-16 max-w-4xl mx-auto rounded-sm overflow-hidden border border-slate-700/50 shadow-2xl shadow-black/40"
          style={{ y: imgY }}
          {...fadeInUp}
          transition={{ duration: 0.7, delay: 0.6 }}
        >
          <img
            src={COMPARISON_URL}
            alt="From old approach to new approach"
            data-testid="shift-comparison-image"
            className="w-full h-auto"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-500/40 to-transparent" />
          {/* Decorative corners */}
          <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-gold/40" />
          <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-gold/40" />
        </motion.div>
      </div>
    </section>
  );
}
